import { useState } from 'react'

import { useNavigate } from 'react-router-dom'

import {
  useAuthMember,
  useCastVote,
  usePoll,
  useUpdatePoll,
} from '@tribeplatform/react-sdk/hooks'
import { Poll, PollStatus } from '@tribeplatform/react-sdk/types'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { IconButton } from '@tribeplatform/react-ui-kit/Button'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { logger } from '../common/index.js'
import { dayjs } from '../common/lib/dayjs.js'
import { T, useI18n } from '../i18n/index.js'

import { PollFormDialog } from './PollFormDialog.js'
import { PollOptions } from './PollOptions.js'
import { PollResult } from './PollResult.js'
import { usePollPermission } from './usePollPermission.js'
import { isAlreadyVoted, onError } from './utils.js'

export interface PollProps {
  id: Poll['id']
  interactive?: boolean

  hideResult?: boolean
}

type OnVote = (selectionIds: number[]) => void

export function PollComponent({
  id,
  interactive = true,
  hideResult = false,
}: PollProps) {
  const i18n = useI18n()
  const poll = usePoll(id)
  const { isGuest } = useAuthMember()
  const navigate = useNavigate()
  const { canCreateOrUpdate } = usePollPermission({
    entityType: poll?.parentEntityType,
    entityId: poll?.parentEntityId,
  })

  const { mutateAsync: castVote, isLoading } = useCastVote({
    fields: {
      options: {
        result: 'all',
      },
      usersVote: 'all',
    },
    useMutationOptions: {
      onError: onError(i18n),
    },
  })

  if (!poll || !poll.options) {
    return null
  }

  const onVote: OnVote = selectionIds => {
    if (!interactive) {
      return
    }

    if (isGuest) {
      navigate(`/auth/login`)
      return
    }

    if (!poll.voteToken) {
      logger.warn('Poll vote token is missing')
      return
    }

    castVote({
      input: {
        pollId: poll.id,
        selectionIds,
        voteToken: poll.voteToken,
      },
    })
  }

  const showResult =
    !hideResult && (isAlreadyVoted(poll) || poll.status === PollStatus.Closed)

  const showMenu = canCreateOrUpdate && interactive

  const isScheduled = poll.status === PollStatus.Scheduled

  return (
    <div className="not-prose flex flex-col gap-2 border border-line-subdued rounded-md p-4 my-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg">{poll.title}</h3>
          <MaxVotesHint poll={poll} />
        </div>
        {showMenu && <PollMenu poll={poll} />}
      </div>
      {isScheduled && <PollOpensAt poll={poll} />}
      {showResult ? (
        <PollResult id={id} />
      ) : (
        <PollOptions
          isLoading={isLoading}
          poll={poll}
          onVote={onVote}
          interactive={interactive}
        />
      )}
    </div>
  )
}

function MaxVotesHint({ poll }: { poll: Poll }) {
  if (poll.maxSelections === 1 || poll.status !== PollStatus.Active) {
    return null
  }

  return (
    <p className="text-sm text-content-muted">
      <T
        id="Poll.SelectUpToOptions"
        defaultMessage="Select up to {maxSelections, number} options"
        values={{ maxSelections: poll.maxSelections }}
      />
    </p>
  )
}

function PollOpensAt({ poll }: { poll: Poll }) {
  const { $t } = useI18n()
  const opensAt = poll.opensAt ? new Date(poll.opensAt) : undefined

  if (!opensAt || opensAt < new Date()) {
    return null
  }

  const isStartSoon = dayjs(opensAt).isBefore(dayjs().add(1, 'hour'))

  return (
    <Alert
      title={
        isStartSoon
          ? $t({ id: 'Poll.StartSoon', defaultMessage: 'Poll starts soon' })
          : $t({
              id: 'Poll.StartsAt',
              defaultMessage: 'Poll starts at',
            })
      }
      status="info"
    >
      {opensAt.toLocaleString()}
    </Alert>
  )
}

function PollMenu({ poll }: { poll: Poll }) {
  const i18n = useI18n()
  const [editing, setEditing] = useState(false)

  const { mutateAsync: updatePoll } = useUpdatePoll({
    useMutationOptions: {
      onError: onError(i18n),
    },
  })
  const { $t } = useI18n()

  const canClosePoll = poll.status === PollStatus.Active

  const onClosePoll = () => {
    updatePoll({
      id: poll.id,
      input: {
        closesAt: new Date().toISOString(),
      },
    })
  }

  const hasScheduled = poll.status === PollStatus.Scheduled

  const onOpenNow = () => {
    updatePoll({
      id: poll.id,
      input: { opensAt: new Date().toISOString() },
    })
  }

  return (
    <>
      <Dropdown placement="bottom-end">
        <Dropdown.ButtonMinimal
          aria-label={$t({
            defaultMessage: 'Options',
            id: 'Generics.Options',
          })}
        >
          <IconButton
            variant="tertiaryNeutral"
            size="lg"
            icon={<SvgIcon name="dots-horizontal" />}
            aria-label={$t({
              defaultMessage: 'Options',
              id: 'Generics.Options',
            })}
            as="span"
          />
        </Dropdown.ButtonMinimal>
        <Dropdown.Items>
          <Dropdown.Item onClick={() => setEditing(true)}>
            <T id="Poll.Edit" defaultMessage="Edit" />
          </Dropdown.Item>
          {canClosePoll && (
            <Dropdown.Item onClick={onClosePoll}>
              <T id="Poll.ClosePoll" defaultMessage="Close Poll" />
            </Dropdown.Item>
          )}
          {hasScheduled && (
            <Dropdown.Item onClick={onOpenNow}>
              <T id="Poll.OpenNow" defaultMessage="Open Now" />
            </Dropdown.Item>
          )}
        </Dropdown.Items>
      </Dropdown>
      <PollFormDialog
        id={poll.id}
        open={editing}
        onCancel={() => setEditing(false)}
        poll={{
          entityTypeId: poll.parentEntityId,
          entityType: poll.parentEntityType,
        }}
        onSuccess={() => {
          setEditing(false)
        }}
      />
    </>
  )
}
